import { createContext, useContext, useMemo, useEffect, useCallback, useState, ReactNode } from 'react'

interface LayoutContextType {
  headerTopHeight: number
  headerBottomHeight: number
  setLayoutDimension: (key: 'top' | 'bottom', value: number) => void
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined)

const DEFAULT_TOP = 64
const DEFAULT_BOTTOM = 0

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [topHeight, setTopHeight] = useState(DEFAULT_TOP)
  const [bottomHeight, setBottomHeight] = useState(DEFAULT_BOTTOM)

  const setLayoutDimension = useCallback((key: 'top' | 'bottom', value: number) => {
    if (key === 'top') setTopHeight(value)
    if (key === 'bottom') setBottomHeight(value)
  }, [])

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--header-top-height', `${topHeight}px`)
    root.style.setProperty('--header-bottom-height', `${bottomHeight}px`)
  }, [topHeight, bottomHeight])

  const value = useMemo<LayoutContextType>(() => ({
    headerTopHeight: topHeight,
    headerBottomHeight: bottomHeight,
    setLayoutDimension,
  }), [topHeight, bottomHeight, setLayoutDimension])

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  )
}

export function useLayout(): LayoutContextType {
  const ctx = useContext(LayoutContext)
  if (!ctx) {
    throw new Error('useLayout must be used within LayoutProvider')
  }
  return ctx
}
